import React, { useEffect, useRef, useState } from 'react'
import Navigation from './Router'
import ReactPixel from 'react-facebook-pixel'
import { Amplify } from 'aws-amplify'
import { Hub } from 'aws-amplify/utils'
import { signOut } from '@aws-amplify/auth'
import ApiService from './ApiService'
import useUserStore from './hook/useUserStore'
import { deleteAmplifyCookies } from './util/UtilFunctions'
import { getLastActiveTime, setLastActiveTime, clearAuthSessionState, SESSION_TIMEOUT_MS } from './util/tokenStorage'
import { clearCachedTokens, decodeIdToken, getAccessToken } from './util/authUtil'

ReactPixel.init('155837033166212')
ReactPixel.pageView()

const App = () => {
  const [loading, setLoading] = useState(false)
  const patient = useUserStore(state => state.patient)
  const setPatient = useUserStore(state => state.setPatient)
  const isAuth = useUserStore(state => state.isAuth)
  const setIsAuth = useUserStore(state => state.setIsAuth)
  const isLoggingOutRef = useRef(false)

  useEffect(() => {
    setLoading(true)
    const checkUserSignedIn = async () => {
      try {
        const accessToken = await getAccessToken()
        const decoded = decodeIdToken(accessToken)
        if (accessToken && decoded) {
          setLastActiveTime(new Date().toISOString())
          setIsAuth(true)
        } else {
          setIsAuth(false)
        }
      } catch (error) {
        setIsAuth(false)
      } finally {
        setLoading(false)
      }
    }
    checkUserSignedIn()
  }, [])

  useEffect(() => {
    if (isAuth !== true) {
      setLoading(false)
      return
    }

    setLoading(true)

    const getPatient = async () => {
      try {
        try {
          const accessToken = await getAccessToken()
          const decoded = decodeIdToken(accessToken)
          if (!accessToken || !decoded) {
            setIsAuth(false)
            setLoading(false)
            return
          }
          setLastActiveTime(new Date().toISOString())
        } catch {
          setIsAuth(false)
          setLoading(false)
          return
        }
        const res = await ApiService.getPatientAfterSessRef()
        setPatient(res.data)
      } catch (err) {
        setPatient(null)
      } finally {
        setLoading(false)
      }
    }

    getPatient()
  }, [isAuth])

  useEffect(() => {
    const listener = data => {
      switch (data.payload.event) {
        case 'signIn':
          if (isAuth !== true) setIsAuth(true)
          break
        case 'signOut':
          setIsAuth(false)
          break
        default:
        // do nothing
      }
    }

    const unsubscribe = Hub.listen('auth', listener)

    return () => {
      unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (!isAuth) return
    isLoggingOutRef.current = false

    const updateActivity = () => setLastActiveTime(new Date().toISOString())
    const handleIdleLogout = async () => {
      if (isLoggingOutRef.current) return
      isLoggingOutRef.current = true
      try {
        await signOut({ global: true })
      } catch (err) {
        // ignore signOut errors to continue cleanup
      }
      deleteAmplifyCookies()
      clearAuthSessionState()
      clearCachedTokens()
      setIsAuth(false)
      window.location.replace('/login')
    }

    const interval = setInterval(() => {
      const lastActive = getLastActiveTime()
      if (!lastActive) return
      const lastActiveMs = new Date(lastActive).getTime()
      if (Number.isNaN(lastActiveMs)) return
      if (Date.now() - lastActiveMs > SESSION_TIMEOUT_MS) {
        handleIdleLogout()
      }
    }, 60 * 1000)

    const events = ['click', 'keydown', 'mousemove', 'mousedown', 'scroll', 'touchstart']
    events.forEach(evt => window.addEventListener(evt, updateActivity))

    updateActivity()

    return () => {
      clearInterval(interval)
      events.forEach(evt => window.removeEventListener(evt, updateActivity))
    }
  }, [isAuth])

  return (
    <div className="App">
      <Navigation loginStatus={isAuth} />
    </div>
  )
}

export default App
